import {equals, isNil, isNotNil} from 'ramda';
import {isFalse} from 'ramda-adjunct';

import {FiltersRequestBody} from '../../../types/api/api';
import {FilterFormData} from '../../../types/FilterFormData';

const PERCENTAGE_MULTIPLIER = 100;

const mapLocation = (values: FilterFormData) => {
  if (
    isNil(values?.address) ||
    isNil(values.address.lat) ||
    isNil(values.address.lng) ||
    isNil(values?.radius)
  ) {
    return undefined;
  }
  return {lat: values.address.lat, lon: values.address.lng, distance: values.radius};
};

const mapIsVatDeductible = (isVatDeductible: string[]) => {
  if (equals(isVatDeductible, ['YES'])) {
    return true;
  }
  if (equals(isVatDeductible, ['NO'])) {
    return false;
  }
  return undefined;
};

const mapBodyStyle = (bodyStyles?: string[]): string[] | undefined => {
  if (isNil(bodyStyles) || bodyStyles.length === 0) {
    return undefined;
  }
  if (bodyStyles.includes('CARSTYLE_VAN')) {
    return [...bodyStyles, 'VANSTYLE_VAN'];
  }
  if (bodyStyles.includes('VANSTYLE_VAN')) {
    return [...bodyStyles, 'CARSTYLE_VAN'];
  }
  return bodyStyles;
};

const mapArrayField = <T>(values?: T[] | null): T[] | undefined => {
  if (isNil(values) || values.length === 0) {
    return undefined;
  }
  return values;
};

type NumericRange = {
  gte?: number;
  lte?: number;
};

const mapNumericRange = (
  gte: number | null | undefined,
  lte: number | null | undefined
): NumericRange | undefined => {
  if (isNil(gte) && isNil(lte)) {
    return undefined;
  }

  return {
    gte: gte ?? undefined,
    lte: lte ?? undefined,
  };
};

export const formValuesToRequestValues = (values: FilterFormData): FiltersRequestBody => ({
  location: mapLocation(values),
  bodyStyle: mapBodyStyle(values.bodyStyle),
  title: values.title ?? undefined,
  drive: mapArrayField(values.drive),
  fuelType: mapArrayField(values.fuelType),
  isVatDeductible: mapIsVatDeductible(values.isVatDeductible),
  hideSoldIn30: isFalse(values.showSoldIn30) ? true : undefined,
  hideSoldIn90: isFalse(values.showSoldIn90) ? true : undefined,
  hideStockCars: isFalse(values.showStockCars) ? true : undefined,
  transmission: mapArrayField(values.transmission),
  mileage: mapNumericRange(values.mileageGte, values.mileageLte),
  power: mapNumericRange(values.powerGte, values.powerLte),
  price: mapNumericRange(values.priceGte, values.priceLte),
  firstRegistrationOnYear: mapNumericRange(
    values.firstRegistrationOnYearGte,
    values.firstRegistrationOnYearLte
  ),
  featuresLevel: mapNumericRange(
    isNotNil(values.featureScoreGte) ? values.featureScoreGte / PERCENTAGE_MULTIPLIER : undefined,
    isNotNil(values.featureScoreLte) ? values.featureScoreLte / PERCENTAGE_MULTIPLIER : undefined
  ),
  daysOnStock: mapNumericRange(values.daysInAdvertisementGte, values.daysInAdvertisementLte),
  trimLevel: mapArrayField(values.trimLevels),
  features: mapArrayField(values.features),
  sellerType: mapArrayField(values.sellerType),
  // Keep empty arrays: `server: []` means "no servers selected" and must not be
  // coerced to undefined (StatisticsPage/AdvertisementPage skip queries when empty).
  server: values.server ?? undefined,
});
